import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, TrendingUp, Milestone, Table2, Landmark, Wallet,
  Scale, UploadCloud, History, Settings as SettingsIcon, Sun, Moon,
  Search, LogOut, BarChart3, Focus,
} from "lucide-react";
import { useTheme } from "@/lib/ThemeContext";
import { useFocus } from "@/lib/FocusContext";
import { useAuth } from "@/auth/AuthContext";

/**
 * Cmd-K style command palette — Raycast-inspired.
 * Toggle: ⌘K / Ctrl-K anywhere in the app.
 */
const COMMANDS = (nav, theme, toggleTheme, logout, focus, toggleFocus) => [
  { group: "Navigate", items: [
    { id: "nav-projection",   label: "Projection",              icon: LayoutDashboard, kbd: "G P", run: () => nav("/") },
    { id: "nav-sales",        label: "Sales Bookings",          icon: TrendingUp,      kbd: "G S", run: () => nav("/sales") },
    { id: "nav-collections",  label: "Collections Model",       icon: Table2,          kbd: "G C", run: () => nav("/collections") },
    { id: "nav-milestones",   label: "Milestones",              icon: Milestone,       kbd: "G M", run: () => nav("/milestones") },
    { id: "nav-land",         label: "Land Sales",              icon: Landmark,        run: () => nav("/land-sales") },
    { id: "nav-planning",     label: "Planning · Chairman View", icon: Wallet,         run: () => nav("/planning") },
    { id: "nav-analytics",    label: "Analytics · Intelligence", icon: BarChart3,      kbd: "G A", run: () => nav("/analytics") },
    { id: "nav-actuals",      label: "Actuals vs Projection",   icon: Scale,           run: () => nav("/actuals") },
  ]},
  { group: "System", items: [
    { id: "sys-import",   label: "Excel Import",     icon: UploadCloud,   run: () => nav("/import") },
    { id: "sys-history",  label: "Import History",   icon: History,       run: () => nav("/import-history") },
    { id: "sys-settings", label: "Settings",         icon: SettingsIcon,  run: () => nav("/settings") },
  ]},
  { group: "Preferences", items: [
    { id: "pref-focus", label: focus ? "Exit focus mode" : "Enter focus mode · Chairman view", icon: Focus, kbd: "⌘⇧F", run: toggleFocus },
    { id: "pref-theme", label: theme === "dark" ? "Switch to light theme" : "Switch to dark theme", icon: theme === "dark" ? Sun : Moon, kbd: "⌘⇧L", run: toggleTheme },
    { id: "pref-signout", label: "Sign out", icon: LogOut, run: logout },
  ]},
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();
  const { theme, toggle } = useTheme();
  const { focus, toggleFocus } = useFocus();
  const { logout } = useAuth();

  // Global hotkey ⌘K / Ctrl-K
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (meta && e.shiftKey && (e.key === "l" || e.key === "L")) {
        e.preventDefault();
        toggle();
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  const groups = useMemo(() => {
    const all = COMMANDS(
      nav,
      theme,
      () => { toggle(); setOpen(false); },
      () => { logout(); nav("/login"); },
      focus,
      () => { toggleFocus(); setOpen(false); },
    );
    if (!query.trim()) return all;
    const q = query.toLowerCase();
    return all
      .map((g) => ({ ...g, items: g.items.filter((it) => it.label.toLowerCase().includes(q)) }))
      .filter((g) => g.items.length);
  }, [query, nav, theme, toggle, logout, focus, toggleFocus]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const onKeyInput = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && flat[active]) { e.preventDefault(); flat[active].run(); setOpen(false); }
  };

  if (!open) return null;

  return (
    <div className="cf-cmd-overlay" onClick={() => setOpen(false)} data-testid="command-palette-overlay">
      <div className="cf-cmd cf-scale-in" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Command palette">
        <div className="flex items-center gap-2 pl-4 pr-3 border-b" style={{ borderColor: "var(--cf-hairline)" }}>
          <Search size={15} className="text-muted-light shrink-0" />
          <input
            ref={inputRef}
            data-testid="command-palette-input"
            className="cf-cmd-input border-0"
            placeholder="Search — jump to a page, toggle theme, run an action…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActive(0); }}
            onKeyDown={onKeyInput}
          />
          <span className="cf-cmd-kbd">esc</span>
        </div>
        <div className="cf-cmd-list">
          {groups.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-light">No matches</div>
          )}
          {groups.map((g, gi) => {
            let idx = groups.slice(0, gi).reduce((s, x) => s + x.items.length, 0);
            return (
              <div key={g.group}>
                <div className="cf-cmd-group">{g.group}</div>
                {g.items.map((it) => {
                  const my = idx++;
                  const Icon = it.icon;
                  return (
                    <div
                      key={it.id}
                      className="cf-cmd-item"
                      data-active={my === active}
                      onMouseEnter={() => setActive(my)}
                      onClick={() => { it.run(); setOpen(false); }}
                      data-testid={`cmd-${it.id}`}
                    >
                      <Icon size={15} strokeWidth={1.8} className="shrink-0 opacity-80" />
                      <span className="flex-1 truncate">{it.label}</span>
                      {it.kbd && <span className="cf-cmd-kbd">{it.kbd}</span>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
